'use client';

import { useId, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { AppLogo } from '@kit/ui/patterns/app-logo';

import { WebsiteEditorialScene } from './website-editorial-scene';
import styles from './website-editorial.module.css';
import {
  WebsiteWorkflowDemos,
  type WebsiteWorkflowKind,
} from './website-workflow-demos';

const kinds: WebsiteWorkflowKind[] = [
  'knowledge',
  'conversation',
  'action',
  'monitor',
  'team',
  'escalation',
];
const captions = [
  [
    'Knowledge',
    'A useful foundation.',
    'Answers start with the information you connect.',
  ],
  [
    'Conversation',
    'Ready for the conversation.',
    'Help customers where they are, whenever they need you.',
  ],
  [
    'Action',
    'A connected next step.',
    'Configured tools help your agent move things forward.',
  ],
  [
    'Monitor',
    'You stay in control.',
    'Follow the conversation and bring in your team.',
  ],
  [
    'Guidance',
    'Your insight. Their next step.',
    'Guide your agent privately while the conversation continues.',
  ],
  [
    'Team',
    'The right people, at the right moment.',
    'Bring your team in when a conversation needs human help.',
  ],
];
export function WebsiteEditorialWorkflow() {
  const { t } = useTranslation('marketing');
  const [index, setIndex] = useState(0);
  const id = useId();
  return (
    <div className={styles.workflow}>
      <div
        role="tablist"
        aria-label={t('editorial.workflowLabel')}
        aria-orientation="vertical"
        className={styles.steps}
      >
        {kinds.map((kind, i) => (
          <button
            key={kind}
            id={`${id}-${kind}`}
            role="tab"
            aria-controls={`${id}-panel`}
            aria-selected={index === i}
            tabIndex={index === i ? 0 : -1}
            onClick={() => setIndex(i)}
            onKeyDown={(event) => {
              if (
                ![
                  'ArrowDown',
                  'ArrowUp',
                  'ArrowRight',
                  'ArrowLeft',
                  'Home',
                  'End',
                ].includes(event.key)
              )
                return;
              event.preventDefault();
              const next =
                event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? kinds.length - 1
                    : (i +
                        (['ArrowDown', 'ArrowRight'].includes(event.key)
                          ? 1
                          : -1) +
                        kinds.length) %
                      kinds.length;
              setIndex(next);
              document.getElementById(`${id}-${kinds[next]}`)?.focus();
            }}
          >
            <span>{String(i + 1).padStart(2, '0')}</span>
            <div>
              <strong>{t(`editorial.steps.${kind}.title`)}</strong>
              <p>{t(`editorial.steps.${kind}.description`)}</p>
            </div>
          </button>
        ))}
      </div>
      <div
        id={`${id}-panel`}
        role="tabpanel"
        aria-labelledby={`${id}-${kinds[index]}`}
        className={styles.panel}
      >
        <WebsiteEditorialScene
          footer={
            <footer className={styles.caption}>
              <span className={styles.captionLabel}>
                {String(index + 1).padStart(2, '0')} / {captions[index]![0]}
              </span>
              <h3>{captions[index]![1]}</h3>
              <p>{captions[index]![2]}</p>
              <div className={styles.controls}>
                <button
                  aria-label={t('editorial.previous')}
                  onClick={() =>
                    setIndex((index + kinds.length - 1) % kinds.length)
                  }
                >
                  ←
                </button>
                <span>{String(index + 1).padStart(2, '0')} / 06</span>
                <button
                  aria-label={t('editorial.next')}
                  onClick={() => setIndex((index + 1) % kinds.length)}
                >
                  →
                </button>
              </div>
            </footer>
          }
        >
          <div className={styles.productFrame}>
            <header className={styles.productHeader}>
              <AppLogo className="w-28" />
              <span>{captions[index]![0]}</span>
            </header>
            <WebsiteWorkflowDemos kinds={[kinds[index]!]} />
          </div>
        </WebsiteEditorialScene>
      </div>
    </div>
  );
}
